<template>
  <div class="dashboard-page">
    <!-- Textura sutil, igual que el login -->
    <div class="bg-grid" aria-hidden="true"></div>

    <div class="dashboard-shell">
      <!-- Header -->
      <header class="dash-header fade-in">
        <div class="dash-header-left">
          <div class="dash-logo-wrapper">
            <img :src="urlImagen(logo)" alt="Logo" class="dash-logo" />
          </div>
          <div>
            <h1 class="dash-title">Hola, {{ nombreUsuario }}</h1>
            <p class="dash-sub">{{ nombreGimnasio }}</p>
            <div class="dash-meta">
              <span class="status-pill">
                <span class="status-dot"></span>
                Conectado
              </span>
              <span class="dash-date">{{ fechaActual }}</span>
            </div>
          </div>
        </div>

        <div class="dash-header-right">
          <button
            class="icon-btn"
            @click="modalMembresiasPorVencer = true"
            :disabled="membresiasPorVencer.length === 0"
          >
            <v-badge
              :content="membresiasPorVencer.length"
              :value="membresiasPorVencer.length"
              color="#C8FF4D"
              overlap
            >
              <v-icon color="#8C8C93">mdi-alert-circle-outline</v-icon>
            </v-badge>
          </button>
        </div>
      </header>

      <!-- Métricas de Visitas -->
      <section class="dash-section fade-in delay-100">
        <div class="section-eyebrow">
          <span class="tick-mark"></span>
          Métricas del día
        </div>

        <div class="stats-grid">
          <div
            v-for="(carta, index) in datosVisitas"
            :key="'visita-' + index"
            class="stat-card"
            :class="{
              'is-clickable': carta.accion,
              'is-warning': carta.tendencia === 'warning'
            }"
            :style="{ animationDelay: carta.delay + 's' }"
            @click="carta.accion && carta.accion()"
          >
            <div class="stat-top">
              <div class="stat-icon">
                <v-icon size="18" color="#F2F2ED">{{ carta.icono }}</v-icon>
              </div>
              <span v-if="carta.tendencia === 'up'" class="trend trend-up">
                <v-icon size="12">mdi-trending-up</v-icon>
              </span>
              <span
                v-else-if="carta.tendencia === 'warning'"
                class="trend trend-warning"
              >
                <v-icon size="12">mdi-alert</v-icon>
              </span>
            </div>
            <div class="stat-value">{{ carta.total }}</div>
            <div class="stat-label">{{ carta.nombre }}</div>
            <div class="stat-sub">{{ carta.subtitulo }}</div>
          </div>
        </div>
      </section>

      <!-- Métricas Financieras -->
      <section class="dash-section fade-in delay-200">
        <div class="section-eyebrow">
          <span class="tick-mark"></span>
          Métricas financieras
        </div>

        <div class="stats-grid">
          <div
            v-for="(carta, index) in datosPagos"
            :key="'pago-' + index"
            class="stat-card"
            :style="{ animationDelay: 0.2 + carta.delay + 's' }"
          >
            <div class="stat-top">
              <div class="stat-icon stat-icon-accent">
                <v-icon size="18" color="#0E0E10">{{ carta.icono }}</v-icon>
              </div>
              <span v-if="carta.tendencia === 'up'" class="trend trend-up">
                <v-icon size="12">mdi-trending-up</v-icon>
              </span>
            </div>
            <div class="stat-value">{{ carta.total }}</div>
            <div class="stat-label">{{ carta.nombre }}</div>
            <div class="stat-sub">{{ carta.subtitulo }}</div>
          </div>
        </div>
      </section>

      <!-- Gráficos de Visitas -->
      <section class="dash-section fade-in delay-300">
        <div class="section-eyebrow">
          <span class="tick-mark"></span>
          Análisis de visitas
        </div>

        <div class="charts-grid">
          <div class="chart-card">
            <div class="chart-card-head">
              <v-icon size="16" color="#8C8C93">mdi-clock-outline</v-icon>
              <h3 class="chart-title">Visitas por hora</h3>
            </div>
            <sparkline-component
              :etiquetas="etiquetasVisitasHora"
              :valores="valoresVisitasHora"
              :color="'#C8FF4D'"
              :titulo="'Visitas por hora'"
              :subtitulo="'Visitas registradas por hora'"
            />
          </div>

          <div class="chart-card">
            <div class="chart-card-head">
              <v-icon size="16" color="#8C8C93">mdi-calendar-week</v-icon>
              <h3 class="chart-title">Visitas semana</h3>
            </div>
            <sparkline-component
              :etiquetas="etiquetasVisitasSemana"
              :valores="valoresVisitasSemana"
              :color="'#C8FF4D'"
              :titulo="'Visitas semana'"
              :subtitulo="'Visitas registradas en esta semana'"
            />
          </div>

          <div class="chart-card">
            <div class="chart-card-head">
              <v-icon size="16" color="#8C8C93">mdi-calendar-month</v-icon>
              <h3 class="chart-title">Visitas mes</h3>
            </div>
            <sparkline-component
              :etiquetas="etiquetasVisitasMes"
              :valores="valoresVisitasMes"
              :color="'#C8FF4D'"
              :titulo="'Visitas mes'"
              :subtitulo="'Visitas registradas por días de este mes'"
            />
          </div>
        </div>
      </section>

      <!-- Gráficos de Pagos -->
      <section class="dash-section fade-in delay-400">
        <div class="section-eyebrow">
          <span class="tick-mark"></span>
          Análisis de pagos
        </div>

        <div class="charts-grid">
          <div class="chart-card">
            <div class="chart-card-head">
              <v-icon size="16" color="#8C8C93">mdi-cash</v-icon>
              <h3 class="chart-title">Pagos semana</h3>
            </div>
            <sparkline-component
              :etiquetas="etiquetasPagosSemana"
              :valores="valoresPagosSemana"
              :color="'#C8FF4D'"
              :titulo="'Pagos semana'"
              :subtitulo="'Pagos registrados esta semana'"
            />
          </div>

          <div class="chart-card">
            <div class="chart-card-head">
              <v-icon size="16" color="#8C8C93">mdi-calendar-month</v-icon>
              <h3 class="chart-title">Pagos mes</h3>
            </div>
            <sparkline-component
              :etiquetas="etiquetasPagosMes"
              :valores="valoresPagosMes"
              :color="'#C8FF4D'"
              :titulo="'Pagos mes'"
              :subtitulo="'Pagos registrados este mes'"
            />
          </div>

          <div class="chart-card">
            <div class="chart-card-head">
              <v-icon size="16" color="#8C8C93">mdi-chart-line</v-icon>
              <h3 class="chart-title">Pagos por meses</h3>
            </div>
            <sparkline-component
              :etiquetas="etiquetasPagosMeses"
              :valores="valoresPagosMeses"
              :color="'#C8FF4D'"
              :titulo="'Pagos por meses'"
              :subtitulo="'Pagos registrados por meses de este año'"
            />
          </div>
        </div>
      </section>
    </div>

    <!-- Loading Overlay -->
    <div v-if="cargando" class="loading-overlay">
      <div class="loading-inner">
        <div class="spinner"></div>
        <p class="loading-text">Cargando datos del gimnasio…</p>
      </div>
    </div>

    <!-- Modal de Membresías por Vencer -->
    <v-dialog v-model="modalMembresiasPorVencer" max-width="620px">
      <div class="modal-panel">
        <div class="modal-head">
          <div>
            <h3 class="modal-title">Membresías por vencer</h3>
            <p class="modal-count">{{ membresiasPorVencer.length }} miembros</p>
          </div>
        </div>

        <div class="modal-body">
          <v-data-table
            :headers="headersMembresias"
            :items="membresiasPorVencer"
            :items-per-page="10"
            class="dash-table"
          >
            <template v-slot:item.nombre="{ item }">
              <span class="table-text">{{ item.nombre }}</span>
            </template>
            <template v-slot:item.fechaFin="{ item }">
              <span :class="['status-tag', getBadgeClassEstado(item.fechaFin)]">
                {{ fechaFormateada(item.fechaFin) }}
              </span>
            </template>
          </v-data-table>
        </div>

        <div class="modal-footer">
          <button class="btn-primary" @click="exportarMembresias">
            <v-icon size="16" class="mr-2">mdi-export</v-icon>
            Exportar
          </button>
          <button class="btn-ghost" @click="modalMembresiasPorVencer = false">
            Cerrar
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import HttpService from "../Servicios/HttpService";
import Utiles from "../Servicios/Utiles";
import CartasPersonalizadas from "./Dialogos/CartasPersonalizadas.vue";
import SparklineComponent from "./Dialogos/SparklineComponent.vue";
import SelectorTema from "@/components/SelectorTema.vue";
import { formatearFechaHora } from "@/utils/fechas";

export default {
  name: "DashboardGimnasio",
  components: { CartasPersonalizadas, SparklineComponent, SelectorTema },

  data: () => ({
    nombreGimnasio: "",
    nombreUsuario: localStorage.getItem("usuario"),
    logo: "",
    cargando: false,
    fechaActual: new Date().toLocaleDateString("es-ES", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric"
    }),
    datosVisitas: [],
    datosPagos: [],
    etiquetasVisitasHora: [],
    valoresVisitasHora: [],
    etiquetasVisitasSemana: [],
    valoresVisitasSemana: [],
    etiquetasVisitasMes: [],
    valoresVisitasMes: [],
    etiquetasPagosSemana: [],
    valoresPagosSemana: [],
    etiquetasPagosMes: [],
    valoresPagosMes: [],
    etiquetasPagosMeses: [],
    valoresPagosMeses: [],
    membresiasPorVencer: [],
    modalMembresiasPorVencer: false,
    headersMembresias: [
      { text: "Matrícula", value: "matricula", align: "start" },
      { text: "Nombre", value: "nombre" },
      { text: "Membresía", value: "nombre_membresia" },
      { text: "Fecha Fin", value: "fechaFin", sortable: true }
    ]
  }),

  mounted() {
    this.obtenerDatos();
    this.cargarDatosGimnasio();
  },

  methods: {
    cargarDatosGimnasio() {
      const nombre = localStorage.getItem("nombreGimnasio");
      const logo = localStorage.getItem("logoGimnasio");

      if (nombre && logo) {
        this.nombreGimnasio = nombre;
        this.logo = logo;
        return;
      }


      HttpService.obtenerConDatos({ metodo: "obtener" }, "ajustes.php").then(
        ajustes => {
          this.nombreGimnasio = ajustes.nombre;
          this.logo = ajustes.logo;
          localStorage.setItem("nombreGimnasio", ajustes.nombre);
          localStorage.setItem("logoGimnasio", ajustes.logo);
        }
      );
    },

    fechaFormateada(fecha) {
      return formatearFechaHora(fecha);
    },

    obtenerDatos() {
      this.cargando = true;

      HttpService.obtenerConDatos(
        { metodo: "membresias_por_vencer" },
        "miembros.php"
      ).then(resultado => {
        this.membresiasPorVencer = resultado.map(item => ({
          ...item,
          fechaFinDate: new Date(item.fechaFin)
        }));

        HttpService.obtenerConDatos({ metodo: "obtener" }, "inicio.php").then(
          resultado => {
            this.crearCartas(resultado.datosVisitas, resultado.datosPagos);
            this.etiquetasVisitasHora = Utiles.obtenerClaves(
              resultado.visitasHora
            );
            this.valoresVisitasHora = Utiles.obtenerValores(
              resultado.visitasHora
            );
            let visitasSemana = Utiles.cambiarDiaSemana(
              resultado.visitasSemana
            );
            this.etiquetasVisitasSemana = Utiles.obtenerClaves(visitasSemana);
            this.valoresVisitasSemana = Utiles.obtenerValores(visitasSemana);
            this.etiquetasVisitasMes = Utiles.obtenerClaves(
              resultado.visitasMes
            );
            this.valoresVisitasMes = Utiles.obtenerValores(
              resultado.visitasMes
            );
            let pagosSemana = Utiles.cambiarDiaSemana(resultado.pagosSemana);
            this.etiquetasPagosSemana = Utiles.obtenerClaves(pagosSemana);
            this.valoresPagosSemana = Utiles.obtenerValoresPagos(pagosSemana);
            this.etiquetasPagosMes = Utiles.obtenerClaves(resultado.pagosMes);
            this.valoresPagosMes = Utiles.obtenerValoresPagos(
              resultado.pagosMes
            );
            let pagosMeses = Utiles.cambiarNumeroANombreMes(
              resultado.pagosMeses
            );
            this.etiquetasPagosMeses = Utiles.obtenerClaves(pagosMeses);
            this.valoresPagosMeses = Utiles.obtenerValoresPagos(pagosMeses);
            this.cargando = false;
          }
        );
      });
    },

    crearCartas(visitas, pagos) {
      this.datosVisitas = [
        {
          icono: "mdi-calendar-check",
          nombre: "Visitas Hoy",
          total: visitas.visitasHoy,
          tendencia: "up",
          delay: 0.1
        },
        {
          icono: "mdi-calendar-range",
          nombre: "Visitas Semana",
          total: visitas.visitasSemana,
          tendencia: "up",
          delay: 0.15
        },
        {
          icono: "mdi-calendar-month",
          nombre: "Visitas Mes",
          total: visitas.visitasMes,
          tendencia: "up",
          delay: 0.2
        },
        {
          icono: "mdi-clock-alert",
          nombre: "Membresías por Vencer",
          total: this.membresiasPorVencer.length,
          tendencia: this.membresiasPorVencer.length > 5 ? "warning" : "stable",
          accion: () => (this.modalMembresiasPorVencer = true),
          delay: 0.25
        }
      ];

      this.datosPagos = [
        {
          icono: "mdi-currency-usd",
          nombre: "Total Ingresos",
          total: "$" + this.formatMoney(pagos.totalPagos),
          tendencia: "up",
          delay: 0.1
        },
        {
          icono: "mdi-cash",
          nombre: "Ingresos Hoy",
          total: "$" + this.formatMoney(pagos.pagosHoy),
          tendencia: "up",
          delay: 0.15
        },
        {
          icono: "mdi-chart-line",
          nombre: "Ingresos Semana",
          total: "$" + this.formatMoney(pagos.pagosSemana),
          tendencia: "up",
          delay: 0.2
        },
        {
          icono: "mdi-chart-bar",
          nombre: "Ingresos Mes",
          total: "$" + this.formatMoney(pagos.pagosMes),
          tendencia: "up",
          delay: 0.25
        }
      ];
    },

    formatMoney(amount) {
      if (!amount) return "0.00";
      return parseFloat(amount).toLocaleString("es-MX", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    getBadgeClassEstado(fechaFin) {
      const fecha = new Date(fechaFin);
      const hoy = new Date();
      const diasRestantes = Math.floor((fecha - hoy) / (1000 * 60 * 60 * 24));

      if (diasRestantes <= 0) return "status-tag-error";
      if (diasRestantes <= 3) return "status-tag-warning";
      if (diasRestantes <= 7) return "status-tag-info";
      return "status-tag-success";
    },

    contactarMiembro(miembro) {
      if (miembro.telefono) {
        window.open(`tel:${miembro.telefono}`, "_blank");
      }
    },

    renovarMembresia(miembro) {
      this.$emit("renovar-membresia", miembro);
    },

    exportarMembresias() {
      const csvContent = this.convertToCSV(this.membresiasPorVencer);
      const blob = new Blob([csvContent], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `membresias_por_vencer_${
        new Date().toISOString().split("T")[0]
      }.csv`;
      a.click();
    },

    convertToCSV(data) {
      const headers = ["Matrícula", "Nombre", "Membresía", "Fecha Fin"];
      const rows = data.map(item => [
        item.matricula,
        item.nombre,
        item.nombre_membresia,
        this.fechaFormateada(item.fechaFin)
      ]);

      return [headers, ...rows].map(row => row.join(",")).join("\n");
    },

    urlImagen(imagen) {
      return Utiles.generarURL(imagen);
    }
  }
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap");

/* ===== TOKENS (mismos del login) ===== */
.dashboard-page {
  --bg: #0e0e10;
  --card: #17171a;
  --card-hover: #1b1b1f;
  --card-border: #26262a;
  --text-primary: #f2f2ed;
  --text-secondary: #8c8c93;
  --text-tertiary: #5c5c62;
  --accent: #c8ff4d;
  --accent-dim: rgba(200, 255, 77, 0.12);
  --warning: #ffb84d;
  --error: #ff6b6b;
  --radius: 4px;

  --font-display: "Space Grotesk", sans-serif;
  --font-body: "Inter", -apple-system, BlinkMacSystemFont, sans-serif;
  --font-mono: "JetBrains Mono", monospace;
}

.dashboard-page {
  min-height: 100vh;
  background: var(--bg);
  color: var(--text-primary);
  font-family: var(--font-body);
  position: relative;
  padding: 40px;
}

.bg-grid {
  position: fixed;
  inset: 0;
  background-image: linear-gradient(
      rgba(242, 242, 237, 0.02) 1px,
      transparent 1px
    ),
    linear-gradient(90deg, rgba(242, 242, 237, 0.02) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
  z-index: 0;
}

.dashboard-shell {
  position: relative;
  z-index: 1;
  max-width: 1280px;
  margin: 0 auto;
}

/* ===== HEADER ===== */
.dash-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 24px 28px;
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  margin-bottom: 40px;
}

.dash-header-left {
  display: flex;
  align-items: center;
  gap: 18px;
}

.dash-logo-wrapper {
  width: 52px;
  height: 52px;
  border-radius: var(--radius);
  overflow: hidden;
  border: 1px solid var(--card-border);
  flex-shrink: 0;
}

.dash-logo {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.dash-title {
  font-family: var(--font-display);
  font-size: 20px;
  font-weight: 700;
  letter-spacing: -0.01em;
  margin: 0 0 2px;
  color: var(--text-primary);
}

.dash-sub {
  font-size: 13px;
  color: var(--text-secondary);
  margin: 0 0 8px;
}

.dash-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: var(--font-mono);
  font-size: 10.5px;
  letter-spacing: 0.06em;
  color: var(--text-secondary);
  text-transform: uppercase;
}

.status-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 6px var(--accent);
}

.dash-date {
  font-size: 12px;
  color: var(--text-tertiary);
  text-transform: capitalize;
}

.icon-btn {
  width: 38px;
  height: 38px;
  border-radius: var(--radius);
  border: 1px solid var(--card-border);
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.icon-btn:hover:not(:disabled) {
  border-color: var(--text-tertiary);
  background: rgba(255, 255, 255, 0.02);
}

.icon-btn:disabled {
  opacity: 0.4;
  cursor: default;
}

/* ===== SECTIONS ===== */
.dash-section {
  margin-bottom: 40px;
}

.section-eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: var(--font-mono);
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-bottom: 16px;
}

.tick-mark {
  width: 12px;
  height: 1px;
  background: var(--accent);
}

/* ===== STATS GRID ===== */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 14px;
}

.stat-card {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 20px;
  transition: border-color 0.15s ease, background 0.15s ease,
    transform 0.15s ease;
  animation: fadeInUp 0.4s ease both;
}

.stat-card.is-clickable {
  cursor: pointer;
}

.stat-card.is-clickable:hover {
  border-color: var(--text-tertiary);
  background: var(--card-hover);
  transform: translateY(-2px);
}

.stat-card.is-warning {
  border-color: rgba(255, 184, 77, 0.35);
}

.stat-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.stat-icon {
  width: 34px;
  height: 34px;
  border-radius: var(--radius);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid var(--card-border);
  display: flex;
  align-items: center;
  justify-content: center;
}

.stat-icon-accent {
  background: var(--accent);
  border-color: var(--accent);
}

.trend {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
}

.trend-up {
  color: var(--accent);
  background: var(--accent-dim);
}

.trend-warning {
  color: var(--warning);
  background: rgba(255, 184, 77, 0.12);
}

.stat-value {
  font-family: var(--font-display);
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.stat-sub {
  font-size: 12px;
  color: var(--text-tertiary);
}

/* ===== CHARTS ===== */
.charts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 14px;
}

.chart-card {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 20px;
}

.chart-card-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}

.chart-title {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

/* ===== LOADING ===== */
.loading-overlay {
  position: fixed;
  inset: 0;
  background: rgba(14, 14, 16, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.loading-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 2px solid var(--card-border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.loading-text {
  font-family: var(--font-mono);
  font-size: 12px;
  color: var(--text-secondary);
  letter-spacing: 0.03em;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===== MODAL ===== */
.modal-panel {
  background: var(--card);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  overflow: hidden;
}

.modal-head {
  padding: 22px 24px;
  border-bottom: 1px solid var(--card-border);
}

.modal-title {
  font-family: var(--font-display);
  font-size: 17px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 4px;
}

.modal-count {
  font-family: var(--font-mono);
  font-size: 11px;
  color: var(--text-secondary);
  margin: 0;
}

.modal-body {
  padding: 0;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid var(--card-border);
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  background: var(--accent);
  color: var(--bg);
  border: none;
  border-radius: var(--radius);
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease;
}

.btn-primary:hover {
  filter: brightness(1.08);
}

.btn-ghost {
  background: transparent;
  color: var(--text-secondary);
  border: 1px solid var(--card-border);
  border-radius: var(--radius);
  padding: 9px 16px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.btn-ghost:hover {
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.table-text {
  font-size: 13px;
  color: var(--text-secondary);
}

.status-tag {
  display: inline-flex;
  align-items: center;
  padding: 3px 9px;
  border-radius: var(--radius);
  font-size: 11px;
  font-weight: 600;
  font-family: var(--font-mono);
}

.status-tag-error {
  background: rgba(255, 107, 107, 0.12);
  color: var(--error);
}

.status-tag-warning {
  background: rgba(255, 184, 77, 0.12);
  color: var(--warning);
}

.status-tag-info {
  background: rgba(140, 140, 147, 0.16);
  color: var(--text-secondary);
}

.status-tag-success {
  background: var(--accent-dim);
  color: var(--accent);
}

/* Overrides Vuetify data-table */
.dash-table {
  background: transparent !important;
}

.dash-table >>> .v-data-table__wrapper {
  background: transparent;
}

.dash-table >>> table {
  background: transparent;
}

.dash-table >>> thead th {
  background: rgba(255, 255, 255, 0.02) !important;
  color: var(--text-tertiary) !important;
  text-transform: uppercase;
  font-size: 10px !important;
  letter-spacing: 0.08em;
  font-family: var(--font-mono);
  border-bottom: 1px solid var(--card-border) !important;
}

.dash-table >>> tbody td {
  color: var(--text-secondary) !important;
  border-bottom: 1px solid var(--card-border) !important;
  font-size: 13px;
}

.dash-table >>> tbody tr:hover {
  background: rgba(255, 255, 255, 0.02) !important;
}

.dash-table >>> .v-data-footer {
  color: var(--text-tertiary) !important;
  border-top: 1px solid var(--card-border) !important;
}

/* ===== MOTION ===== */
.fade-in {
  animation: fadeIn 0.5s ease both;
}

.delay-100 {
  animation-delay: 0.06s;
}
.delay-200 {
  animation-delay: 0.12s;
}
.delay-300 {
  animation-delay: 0.18s;
}
.delay-400 {
  animation-delay: 0.24s;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in,
  .stat-card,
  .spinner {
    animation: none;
  }
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .dashboard-page {
    padding: 20px;
  }

  .dash-header {
    flex-direction: column;
    align-items: flex-start;
    padding: 20px;
  }

  .stats-grid,
  .charts-grid {
    grid-template-columns: 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/InicioComponent.vue